<template>
  <v-row dense>
    <v-col :cols="12" v-for="(price, index) in priceList" :key="index">
      <v-card class="pa-1">
        <v-row>
          <v-col :cols="8">
            <div class="ml-2 mt-2">
              <div class="mb-0 pb-0" :style="{
                fontSize: $vuetify.display.smAndDown
                        ? '1.2rem'
                        : undefined
              }">
                <text-notice-location :all="price.notices" location="prepend-title"/>
                {{ String(price.merchant.name).toUpperCase() }}
                <text-notice-location :all="price.notices" location="append-title"/>
              </div>
              <small
                class="text-disabled"
                style="font-size: 0.8rem"
                v-if="price.last_purchase_at"
              >
                {{
                  $t('product-detail-price-table-v2.last-purchase-at', {datetime: filterDateTime(price.last_purchase_at)})
                }}
              </small>
              <small
                class="text-disabled"
                style="font-size: 0.8rem"
                v-if="price.last_checked_at"
              >
                {{
                  $t('product-detail-price-table-v2.last-checked-at', {datetime: filterDateTime(price.last_checked_at)})
                }}
              </small>
              <chip-notice-location :all="price.notices" location="default"/>
            </div>
          </v-col>
          <v-col :cols="4">
            <div class="float-right text-center pb-2">
              <v-btn
                @click="() => navigateToPrice(price)"
                target="_blank"
                :size="$vuetify.display.mdAndUp ? 'large' : 'default'"
                rel="nofollow"
                :rounded="false"
                :variant="price.in_stock ? 'elevated' : 'tonal'"
                :color="price.in_stock ? (price.stock === 0 ? 'primary' : 'success') : 'secondary'"
                :append-icon="$vuetify.display.mdAndUp ? 'mdi-cart' : undefined"
                class="mt-2 mx-2"
                :class="{
                    'font-weight-bold':true ?? $vuetify.display.mdAndUp,
                  }"
              >
                {{ $n(price.price, 'currency') }}
              </v-btn>
              <br/>
              <small class="text-disabled pb-2" v-if="price.stock">
                &nbsp;{{ price.stock }}
              </small>
            </div>
          </v-col>
        </v-row>
      </v-card>

    </v-col>

    <v-col :cols="12" v-if="!showAll && hasMore">
      <v-btn @click="showAll = true" variant="plain" append-icon="mdi-chevron-down" block>
        {{ $t('product-detail-price-list.btn.show-more', {total: prices.length - size}) }}
      </v-btn>
    </v-col>
    <v-col :cols="12" v-if="showAll && hasMore">
      <v-btn @click="showAll = false" variant="plain" append-icon="mdi-chevron-up" block>
        {{ $t('product-detail-price-list.btn.hide') }}
      </v-btn>
    </v-col>

  </v-row>
</template>
<script setup lang="ts">
import {computed, onBeforeMount, ref} from "vue";
import {filterDateTime} from "@/lib/filters";
import {Configuration, Product} from "@/lib/keys";
import TextNoticeLocation from "@/components/shared/text-notice-location.vue";
import ChipNoticeLocation from "@/components/shared/chip-notice-location.vue";
import {useGtag} from "vue-gtag-next";
import {useThrottleFn} from "@vueuse/core";


interface Props {
  product: Product;
  prices: Configuration[];
  size?: number;
}

const props = withDefaults(defineProps<Props>(), {
  size: 5,
});

const gtag = useGtag();
const alreadyTagged = ref<{ [key: string]: boolean }>({});
const showAll = ref(false);
const hasMore = computed(() => props.prices.length > props.size);
const priceList = computed((): Configuration[] => {
  if (showAll.value) {
    return props.prices;
  }

  return props.prices.slice(0, props.size);
});


const confirmRedirect = useThrottleFn((price: Configuration) => {
  const taggingKey = JSON.stringify(price);

  if (alreadyTagged.value[taggingKey]) {
    return;
  }

  alreadyTagged.value[taggingKey] = true;
  gtag.event('redirect_to_merchant', {
    value: price.price.toString(),
    currency: 'NOK',
    items: [{
      id: props.product.slug,
      name: props.product.name,
      brand: props.product.brand.name,
      item_id: props.product.slug,
      item_name: props.product.name,
      item_brand: props.product.brand.name,
      item_variant: price.merchant.slug,
      location_id: price.merchant_id,
      index: 1,
      quantity: 1,
      price: price.price.toString(),
    }],
  });
}, 250)


const navigateToPrice = async (price: Configuration) => {
  await confirmRedirect(price);
  window.open(price.detail_url, '_blank');
}

onBeforeMount(() => alreadyTagged.value = {})

</script>
